import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, IconButton, Chip } from "@mui/material";
import { Edit } from "@mui/icons-material";
import CustomButton from "../../../components/common/button/CustomButton";
import useStyles from "./complaintModalStyle";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import ManageComplaint from "./ManageComplaint";
import ManageTask from "./ManageTask";
import ManageCosts from "./ManageCosts";
import { useDispatch } from "react-redux";
import {
  getComplaintCostsAction,
  getComplaintInProgressAction,
  getComplaintTaskAction,
  deleteComplaintTaskAction,
  addComplaintAction,
  setDeleteComplaintTaskResponse,
  addComplaintCostAction,
  setAddComplaintCostResponseAction,
  addComplaintInProgressAction,
  setAddComplaintInProgressResponseAction,
  getComplaintByIdAction,
} from "../../../store/reducers/complaintSlice";
import DeleteAlert from "../../../components/common/DeleteAlert";
import {
  addWorkDairyAction,
  setWorkDiaryResponseAction,
  setUpdateWorkDiaryResponseAction,
  updateWorkDairyAction,
} from "../../../store/reducers/workDiarySlice";
import FormField from "../../../components/common/FormField";
import { PROGRESS_FORM_ELEMENT_FIELDS } from "./complaintAllFields";
import { getAllSubUserAction } from "../../../store/reducers/subUserSlice";
import Notification from "../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";

interface ComplaintDetailModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  complaintData: any;
}

const ComplaintDetailModal: React.FC<ComplaintDetailModalProps> = ({
  show,
  setShow,
  complaintData,
}) => {
  const classes = useStyles({ show });
  const dispatch = useDispatch();
  const {
    singleComplaint,
    complaintTasks,
    complaintCosts,
    complaintInProgress,
    isDeleteComplaintSuccess,
    isAddComplaintCostSuccess,
    complaintInProgressResponse,
  } = useSelector((state: any) => state?.complaint);
  const { isWorkDiarySuccess, isWorkDiaryUpdateSuccess } = useSelector(
    (state: any) => state?.workDiary,
  );
  const { userProfile } = useSelector((state: any) => state?.user);
  const { users } = useSelector((state: any) => state.subUser);
  const [formData, setFormData] = useState<any>(complaintData || {});
  const [editModalShow, setEditModalShow] = useState(false);
  const [taskModalShow, setTaskModalShow] = useState(false);
  const [costModalShow, setCostModalShow] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedCost, setSelectedCost] = useState<any>(null);
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [progressInput, setProgressInput] = useState<any>({
    date: "",
    description: "",
    added_by: "",
  });
  const [progressErrors, setProgressErrors] = useState<Record<string, string>>(
    {},
  );
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const complaintId = complaintData?.complaint_id;
  const dateFormat = userProfile?.dateFormat || "DD/MM/YYYY";

  const progressFields = useMemo(() => {
    if (!users || users.length === 0) {
      return PROGRESS_FORM_ELEMENT_FIELDS;
    }

    return PROGRESS_FORM_ELEMENT_FIELDS.map((field) => {
      if (field.id === "added_by") {
        return {
          ...field,
          options: users.map((user: any) => ({
            label: user.name,
            id: user.staff_id ?? user?.tenant_id,
          })),
        };
      }

      return field;
    });
  }, [users]);

  const formatDate = useCallback(
    (value: any) => (value ? dayjs(value).format(dateFormat) : "N/A"),
    [dateFormat],
  );

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "completed") return { bg: "#d4edda", color: "#155724" };
    if (statusLower === "in progress")
      return { bg: "#cce5ff", color: "#004085" };
    if (statusLower === "pending") return { bg: "#fff3cd", color: "#856404" };
    if (statusLower === "cancelled") return { bg: "#f8d7da", color: "#721c24" };
    return { bg: "#e2e3e5", color: "#383d41" }; // default
  };

  useEffect(() => {
    if (complaintData) {
      setFormData(complaintData);
    }
  }, [complaintData]);

  useEffect(() => {
    if (
      singleComplaint?.complaint_id &&
      String(singleComplaint.complaint_id) === String(complaintId)
    ) {
      setFormData((prev: any) => ({
        ...prev,
        ...singleComplaint,
      }));
    }
  }, [singleComplaint, complaintId]);

  useEffect(() => {
    dispatch(getAllSubUserAction({ status: "ACTIVE" }));
  }, [dispatch]);

  useEffect(() => {
    if (
      (isWorkDiarySuccess ||
        isWorkDiaryUpdateSuccess ||
        isDeleteComplaintSuccess) &&
      complaintId
    ) {
      dispatch(
        getComplaintTaskAction({
          complaint_id: complaintId,
          start: null,
          end: null,
        }),
      );
      
      dispatch(setWorkDiaryResponseAction(false));
      dispatch(setUpdateWorkDiaryResponseAction(false));
      dispatch(setDeleteComplaintTaskResponse(false));
      setSelectedTask(null);
      setTaskModalShow(false);
      setDeleteConfirmShow(false);
      setTaskToDelete(null);
    }
  }, [
    complaintId,
    dispatch,
    isDeleteComplaintSuccess,
    isWorkDiarySuccess,
    isWorkDiaryUpdateSuccess,
  ]);

  useEffect(() => {
    if (isAddComplaintCostSuccess) {
      dispatch(getComplaintCostsAction({ complaint_id: complaintId }));
      dispatch(getComplaintByIdAction({ complaint_id: complaintId }));
      setSelectedCost(null);
      setCostModalShow(false);
      dispatch(setAddComplaintCostResponseAction(false));
    }
  }, [complaintId, dispatch, isAddComplaintCostSuccess]);

  useEffect(() => {
    if (complaintInProgressResponse) {
      dispatch(
        getComplaintInProgressAction({
          complaint_id: complaintId,
        }),
      );

      const userId = userProfile?.staff_id ?? userProfile?.tenant_id ?? "";
      setProgressInput((prev: any) => ({
        ...prev,
        complaint_id: complaintId,
        date: dayjs().format("YYYY-MM-DD"),
        description: "",
        added_by: prev?.added_by || userId,
      }));
      setProgressErrors({});

      dispatch(setAddComplaintInProgressResponseAction(false));
    }
  }, [complaintId, complaintInProgressResponse, dispatch, userProfile]);

  useEffect(() => {
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    if (show && complaintId) {
      const startDate = dayjs().format("YYYY-MM-DD");
      const endDate = dayjs().add(7, "day").format("YYYY-MM-DD");
      dispatch(
        getComplaintTaskAction({
          complaint_id: complaintId,
          start: startDate,
          end: endDate,
        }),
      );
      dispatch(getComplaintCostsAction({ complaint_id: complaintId }));
      dispatch(
        getComplaintInProgressAction({
          complaint_id: complaintId,
        }),
      );
      dispatch(getComplaintByIdAction({ complaint_id: complaintId }));
    }
  }, [show, complaintId, dispatch]);

  useEffect(() => {
    if (complaintId && userProfile && users?.length > 0) {
      const userId = userProfile?.staff_id ?? userProfile?.tenant_id;

      setProgressInput((prev: any) => ({
        ...prev,
        complaint_id: complaintId,
        added_by: userId, // set after users loaded
        date: dayjs().format("YYYY-MM-DD"),
      }));
    }
  }, [complaintId, userProfile, users]);

  const handleClose = () => {
    setShow(false);
  };

  const handleEditClick = () => {
    setEditModalShow(true);
  };

  const handleEditModalClose = () => {
    setEditModalShow(false);
  };

  const handleEditSave = (data: any) => {
    setFormData(data);
    // TODO: Implement API call to save changes
    dispatch(addComplaintAction(data));
  };

  const handleAddTask = () => {
    dispatch(setWorkDiaryResponseAction(false));
    dispatch(setUpdateWorkDiaryResponseAction(false));
    setSelectedTask(complaintData);
    setTaskModalShow(true);
  };

  const handleTaskModalClose = () => {
    setTaskModalShow(false);
    setSelectedTask(null);
  };

  const handleTaskSave = (data: any) => {
    if (data?.task_id) {
      dispatch(updateWorkDairyAction(data));
      return;
    }

    dispatch(addWorkDairyAction(data));
  };

  const handleAddCost = () => {
    dispatch(setAddComplaintCostResponseAction(false));
    setSelectedCost(complaintData);
    setCostModalShow(true);
  };

  const handleCostModalClose = () => {
    setCostModalShow(false);
    setSelectedCost(null);
  };

  const handleCostSave = (data: any) => {
    // TODO: Implement API call to save cost
    dispatch(addComplaintCostAction(data));
  };

  if (!show) return null;

  // handle edit task
  const handleEditTask = (task: any) => {
    dispatch(setWorkDiaryResponseAction(false));
    dispatch(setUpdateWorkDiaryResponseAction(false));
    setSelectedTask(task);
    setTaskModalShow(true);
  };

  // handle delete task - show confirmation modal
  const handleDeleteTask = (task_id: any) => {
    setTaskToDelete(task_id);
    setDeleteConfirmShow(true);
  };

  // confirm delete task
  const confirmDeleteTask = () => {
    if (taskToDelete && complaintId) {
      dispatch(
        deleteComplaintTaskAction({
          task_id: taskToDelete,
          complaint_id: complaintId,
        }),
      );
    }
  };

  const handleAddProgress = () => {
    const nextErrors: Record<string, string> = {};

    if (!progressInput?.date) {
      nextErrors.date = "Date is required";
    }

    if (!String(progressInput?.description || "").trim()) {
      nextErrors.description = "Details are required";
    }

    if (!progressInput?.added_by) {
      nextErrors.added_by = "User is required";
    }

    setProgressErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (complaintId) {
      dispatch(
        addComplaintInProgressAction({
          complaint_id: complaintId,
          ...progressInput,
        }),
      );
    }
  };

  const handleInputInprogressChange = (field: string, value: any) => {
    setProgressInput((prevState: any) => ({
      ...prevState,
      [field]: value,
    }));
    setProgressErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleResetProgress = () => {
    const userId = userProfile?.staff_id ?? userProfile?.tenant_id ?? "";

    setProgressInput((prev: any) => ({
      ...prev,
      complaint_id: complaintId,
      date: dayjs().format("YYYY-MM-DD"),
      description: "",
      added_by: userId || prev?.added_by || "",
    }));

    setProgressErrors({});
  };

  return (
    <>
      <Notification />
      <section className={show ? classes.complaintPopupOverlay : ""}>
        <div className={classes.complaintPopupContainer}>
          {/* Header */}
          <div className={classes.popupTitle}>
            <Typography variant="h6">
              Complaint Details - ID: {formData?.complaint_id || "N/A"}
            </Typography>
            <span className={classes.closeButton} onClick={handleClose}>
              ✕
            </span>
          </div>

          {/* Content */}
          <Box sx={{ marginTop: "60px", paddingBottom: "80px" }}>
            <Grid container spacing={2}>
              {/* Complaint Detail Section */}
              <Grid item xs={12}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>
                    <Typography variant="h6">Complaint Detail</Typography>
                    <IconButton
                      size="small"
                      onClick={handleEditClick}
                      sx={{
                        backgroundColor: "#02906f",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#027a5e" },
                        boxShadow: "0 2px 6px rgba(2, 144, 111, 0.3)",
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Complaint ID:</label>
                        <span>{formData?.complaint_id || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Lead ID:</label>
                        <span>{formData?.lead_id || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Customer Name:</label>
                        <span>{formData?.customer_name || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Service:</label>
                        <span>{formData?.job_name || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Service Date:</label>
                        <span>{formatDate(formData?.job_date)}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Reported Date:</label>
                        <span>{formatDate(formData?.reported_date)}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Title:</label>
                        <span>{formData?.title || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Reported By:</label>
                        <span>{formData?.reported_by || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Insurance:</label>
                        <span>{formData?.insurance_type || "N/A"}</span>
                      </div>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Handler:</label>
                        <span>{formData?.handled_by_name || "N/A"}</span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Costs:</label>
                        <span className={classes.costHighlight}>
                          £{formData?.costs || "0.00"}
                        </span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Recovered:</label>
                        <span className={classes.costHighlight}>
                          £{formData?.recovered || "0.00"}
                        </span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Crew:</label>
                        <span>
                          {formData?.crew?.map((c: any) => c.name).join(", ") ||
                            "N/A"}
                        </span>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <div className={classes.detailRow}>
                        <label>Status:</label>
                        <span>{formData?.status || "N/A"}</span>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {/* Tasks Section */}
              <Grid item xs={12}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>
                    <Typography variant="h6">Tasks</Typography>
                    <CustomButton
                      title="+ New Task"
                      variant="contained"
                      onClick={handleAddTask}
                      sx={{
                        backgroundColor: "#02906f",
                        "&:hover": { backgroundColor: "#027a5e" },
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(2, 144, 111, 0.3)",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    />
                  </div>
                  <div className={classes.tableContainer}>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Added By</th>
                          <th>Status</th>
                          <th>Assigned to</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaintTasks.length > 0 ? (
                          complaintTasks.map((task: any) => (
                            <tr key={task.task_id}>
                              <td>
                                {formatDate(task.date)}
                              </td>
                              <td>{task.added_by_name || "N/A"}</td>
                              <td>
                                {task.status ? (
                                  (() => {
                                    const statusColor = getStatusColor(task.status);
                                    return (
                                  <Chip
                                    label={task.status}
                                    size="small"
                                    sx={{
                                      backgroundColor: statusColor.bg,
                                      color: statusColor.color,
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      height: "24px",
                                      borderRadius: "12px",
                                    }}
                                  />
                                    );
                                  })()
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>{task.assigned_to_name || "N/A"}</td>
                              <td>
                                <CustomButton
                                  title="Edit"
                                  variant="outlined"
                                  onClick={() => handleEditTask(task)}
                                  sx={{
                                     color: "#027a5e",
                                    "&:hover": { color: "#fff", backgroundColor: "#027a5e" },
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                  }}
                                />
                                <CustomButton
                                  title="Delete"
                                  variant="outlined"
                                  onClick={() => handleDeleteTask(task.task_id)}
                                  sx={{
                                    color: "#d32f2f",
                                    borderColor: "#d32f2f",
                                    "&:hover": {
                                      color: "#fff",
                                      backgroundColor: "#d32f2f",
                                      borderColor: "#d32f2f",
                                    },
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    marginLeft: "8px",
                                  }}
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={4}
                              style={{
                                textAlign: "center",
                                color: "#999",
                                padding: "30px",
                                fontSize: "0.95rem",
                                fontStyle: "italic",
                              }}
                            >
                              No tasks added yet. Click "+ New Task" to add one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Grid>

              {/* Costs Section */}
              <Grid item xs={12}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>
                    <Typography variant="h6">Costs</Typography>
                    <CustomButton
                      title="+ New Cost"
                      variant="contained"
                      onClick={handleAddCost}
                      sx={{
                        backgroundColor: "#02906f",
                        "&:hover": { backgroundColor: "#027a5e" },
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(2, 144, 111, 0.3)",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    />
                  </div>
                  <div className={classes.tableContainer}>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Added by</th>
                          <th>Details</th>
                          <th>Amount</th>
                          <th>Inv Ref</th>
                          {/* <th>Action</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {complaintCosts.length > 0 ? (
                          complaintCosts.map((cost: any) => (
                            <tr key={cost.cost_id}>
                              <td>
                                {formatDate(cost.date)}
                              </td>
                              <td>{cost.added_by_name || "N/A"}</td>
                              <td>{cost.description || "N/A"}</td>
                              <td>£{cost.amount || "0.00"}</td>
                              <td>{cost.invoice_reference || "N/A"}</td>
                              {/* <td>
                                <CustomButton
                                  title="Edit"
                                  variant="contained"
                                  onClick={() => handleEditCost(cost)}
                                  sx={{
                                    backgroundColor: "#02906f",
                                    "&:hover": { backgroundColor: "#027a5e" },
                                    borderRadius: "8px",
                                    boxShadow: "0 2px 6px rgba(2, 144, 111, 0.3)",
                                    textTransform: "none",
                                    fontWeight: 600,
                                  }}
                                />
                                  <CustomButton
                                  title="Delete"
                                  variant="outlined"
                                  onClick={() => handleDeleteCost(cost.cost_id)}
                                  sx={{
                                    color: "#d32f2f",
                                    borderColor: "#d32f2f",
                                    "&:hover": {
                                      color: "#fff",
                                      backgroundColor: "#d32f2f",
                                      borderColor: "#d32f2f",
                                    },
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    marginLeft: "8px",
                                  }}
                                />
                              </td> */}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              style={{
                                textAlign: "center",
                                color: "#999",
                                padding: "30px",
                                fontSize: "0.95rem",
                                fontStyle: "italic",
                              }}
                            >
                              No costs added yet. Click "+ New Cost" to add one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Grid>

              {/* Progress Section - Add Bar above Table */}
              <Grid item xs={12}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>
                    <Typography variant="h6">Progress</Typography>
                  </div>
                  {/* Add Bar */}
                  <Box
                    sx={{
                      padding: "2px 4px",
                      mb: 0,
                      width: "100%",
                    }}
                  >
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: { xs: "stretch", md: "center" },
                        flexWrap: { xs: "wrap", md: "nowrap" },
                      }}
                    >
                      {progressFields?.map((field: any, i: number) => {
                        return (
                          <FormField
                            key={field.id || i}
                            onPaste={(e: any) => e.preventDefault()}
                            onKeyDown={(event: any) => {
                              if (
                                [8, 9, 37, 38, 39, 40, 18, 17].includes(
                                  event.keyCode,
                                ) ||
                                event.ctrlKey
                              )
                                return event;
                              if (
                                field?.patternAllowed &&
                                !field?.patternAllowed.test(event.key)
                              ) {
                                event.preventDefault();
                              } else if (
                                field?.patternAllowed &&
                                !field?.patternAllowed.test(
                                  event.target.value + event.key,
                                )
                              ) {
                                event.preventDefault();
                              }
                            }}
                            {...{
                              ...field,
                              value: progressInput
                                ? progressInput[field?.id]
                                : "",
                              inSingleGridForm: true,
                              options: field?.options,
                              variant: "outlined",
                              onChange: (e: any) =>
                                handleInputInprogressChange(
                                  field.id,
                                  e.target.value,
                                ),
                              isNonMarginTop: true,
                              InputProps: {
                                ...(field?.inputProps && field?.inputProps),
                                sx: { padding: "0px 4px", height: "36px" },
                              },
                              error: Boolean(progressErrors[field?.id]),
                              helperText: progressErrors[field?.id] || "",
                            }}
                          />
                        );
                      })}
                      <Grid
                        item
                        xs={12}
                        md={2}
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "flex-end", md: "flex-start" },
                          alignItems: "center",
                          paddingTop: 0,
                          gap: 1,
                          minHeight: "36px",
                        }}
                      >
                        <CustomButton
                          title="Reset"
                          variant="outlined"
                          sx={{
                            borderRadius: 0,
                            minWidth: 80,
                            fontWeight: 600,
                            height: "36px",
                            color: "#027a5e",
                            borderColor: "#027a5e",
                            mt:'8px',
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#027a5e",
                              borderColor: "#027a5e",
                            },
                          }}
                          onClick={handleResetProgress}
                        />
                        <CustomButton
                          title="Add"
                          variant="contained"
                          sx={{
                            color: "#fff",
                            borderRadius: 0,
                            minWidth: 80,
                            fontWeight: 600,
                            height: "36px",
                            mt:'8px',
                          }}
                          onClick={handleAddProgress}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  {/* Table */}
                  <div className={classes.tableContainer}>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Detail</th>
                          <th>User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaintInProgress.length > 0 ? (
                          complaintInProgress.map((entry: any) => (
                            <tr key={entry.entry_id}>
                              <td>
                                {formatDate(entry.date)}
                              </td>
                              <td>{entry.description || "N/A"}</td>
                              <td>{entry.added_by_name || "N/A"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              style={{
                                textAlign: "center",
                                color: "#999",
                                padding: "30px",
                                fontSize: "0.95rem",
                                fontStyle: "italic",
                              }}
                            >
                              No history available. Use Add above to insert.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Grid>
            </Grid>
          </Box>
        </div>
      </section>
      {editModalShow && (
        <ManageComplaint
          open={editModalShow}
          onHide={handleEditModalClose}
          title="Edit Complaint"
          selectComplaint={formData}
          onSave={handleEditSave}
        />
      )}
      {taskModalShow && (
        <ManageTask
          open={taskModalShow}
          onHide={handleTaskModalClose}
          title={selectedTask?.task_id ? "Edit Task" : "Add Task"}
          selectTask={selectedTask}
          onSave={handleTaskSave}
        />
      )}
      {costModalShow && (
        <ManageCosts
          open={costModalShow}
          onHide={handleCostModalClose}
          title="Add Cost"
          selectCost={selectedCost}
          onSave={handleCostSave}
        />
      )}
      {deleteConfirmShow && (
        <DeleteAlert
          alertModalShow={deleteConfirmShow}
          setAlertModelShow={setDeleteConfirmShow}
          onConfirm={confirmDeleteTask}
          message="Are you sure you want to delete this task?"
        />
      )}
    </>
  );
};

export default ComplaintDetailModal;
